/**
 * Serial number gate — addon checkboxes
 * -------------------------------------
 * The PDP addon rows add a *different* product than the one the product form
 * belongs to, so the form-level gate never sees them. This handles that path.
 *
 * Two halves:
 *
 * 1. Prompt on tick. Checking a gated addon opens the modal. Cancelling
 *    unticks the box, so the UI always matches what will be added.
 *
 * 2. Inject on add. Requests to /cart/add get the serial attached as a line
 *    item property for the matching variant, whatever code issued them.
 *
 * Half 2 is a safety net for addon code we do not control. If you patch the
 * addon's own add-to-cart call to read window.SerialGate.serialFor(variantId),
 * delete the interceptor at the bottom of this file.
 *
 * Load AFTER serial-number-gate.js.
 */
(function () {
  'use strict';

  var CHECKBOX = '[data-addon-checkbox][data-serial-required="true"]';

  // variantId -> serial, for the current page view.
  var serials = {};

  function debug() {
    if (!window.SERIAL_GATE_DEBUG) return;
    console.log.apply(console, ['[serial-gate:addons]'].concat(Array.prototype.slice.call(arguments)));
  }

  function gate() {
    return window.SerialGate;
  }

  /* ------------------------------------------------------- prompt on tick */

  document.addEventListener(
    'change',
    function (event) {
      var checkbox = event.target;
      if (!checkbox.matches || !checkbox.matches(CHECKBOX)) return;

      var variantId = String(checkbox.getAttribute('data-variant-id') || '');

      if (!checkbox.checked) {
        delete serials[variantId];
        return;
      }

      if (!gate()) {
        console.warn('[serial-gate:addons] serial-number-gate.js is not loaded');
        return;
      }

      gate()
        .prompt({ value: serials[variantId] || '' })
        .then(function (serial) {
          if (!serial) {
            // Backed out — untick and let the addon UI recalculate its total.
            checkbox.checked = false;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }

          serials[variantId] = serial;
          checkbox.setAttribute('data-serial-value', serial);
          debug('serial captured for variant', variantId, serial);
        });
    },
    false
  );

  /* --------------------------------------------------- expose the lookup */

  function serialFor(variantId) {
    return serials[String(variantId)] || null;
  }

  if (gate()) {
    gate().serialFor = serialFor;
    gate().serials = serials;
  }

  /* ------------------------------------------- inject into cart requests */

  var PROPERTY = (gate() && gate().PROPERTY) || 'Serial Number';

  function isCartAdd(url) {
    return String(url || '').indexOf('/cart/add') !== -1;
  }

  /** Mutates a parsed JSON cart payload in place. Returns true if changed. */
  function patchJson(payload) {
    var changed = false;

    var apply = function (item) {
      var serial = serialFor(item.id);
      if (!serial) return;

      item.properties = item.properties || {};
      if (item.properties[PROPERTY]) return;

      item.properties[PROPERTY] = serial;
      changed = true;
    };

    if (Array.isArray(payload.items)) {
      payload.items.forEach(apply);
    } else if (payload.id) {
      apply(payload);
    }

    return changed;
  }

  /** Mutates FormData / URLSearchParams in place. Returns true if changed. */
  function patchFields(body) {
    var changed = false;

    // Single add: a bare `id` field.
    var single = body.get('id');
    if (single && serialFor(single) && !body.get('properties[' + PROPERTY + ']')) {
      body.set('properties[' + PROPERTY + ']', serialFor(single));
      changed = true;
    }

    // Multi add: items[N][id] fields.
    var keys = [];
    body.forEach(function (value, key) {
      keys.push([key, value]);
    });

    keys.forEach(function (pair) {
      var match = pair[0].match(/^items\[(\d+)\]\[id\]$/);
      if (!match) return;

      var serial = serialFor(pair[1]);
      if (!serial) return;

      var propKey = 'items[' + match[1] + '][properties][' + PROPERTY + ']';
      if (body.get(propKey)) return;

      body.set(propKey, serial);
      changed = true;
    });

    return changed;
  }

  var nativeFetch = window.fetch;

  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : input && input.url;

      if (isCartAdd(url) && init && init.body) {
        var body = init.body;

        if (typeof body === 'string') {
          if (body.charAt(0) === '{') {
            var payload = JSON.parse(body);
            if (patchJson(payload)) {
              init = Object.assign({}, init, { body: JSON.stringify(payload) });
              debug('patched JSON body', payload);
            }
          } else {
            var params = new URLSearchParams(body);
            if (patchFields(params)) {
              init = Object.assign({}, init, { body: params.toString() });
              debug('patched urlencoded body');
            }
          }
        } else if (body instanceof FormData) {
          if (patchFields(body)) debug('patched FormData body');
        }
      }
    } catch (error) {
      // Never break a cart request over this.
      console.warn('[serial-gate:addons] could not patch cart request', error);
    }

    return nativeFetch.call(this, input, init);
  };
})();